import ApiClient from '@/utils/ApiClient';
import type {
  ICouponsResponse,
  IPaymentRequest,
  IStripePaymentResponse,
  IPaytrailPaymentResponse,
  IFreemiumResponse,
  ICouponValidationRequest,
  ICouponValidationResponse,
  IPricingSummaryRequest,
  IPricingSummaryResponse,
} from '@/types/payment.types';

const getRequiredHeaders = () => {
  return {
    'x-lang': 'en',
    'x-session-id': localStorage.getItem('sessionId') || '',
    'x-location-name': localStorage.getItem('locationName') || '',
    'x-latitude': localStorage.getItem('latitude') || '',
    'x-longitude': localStorage.getItem('longitude') || '',
    is_admin_panel: 'true',
  };
};

export const PaymentService = {
  /**
   * Fetch available coupons by type
   * @param couponType - The coupon type enum value (1-5)
   */
  getCouponsByType: async (
    couponType: number,
    orderAmount: number
  ): Promise<ICouponsResponse> => {
    try {
      const response = await ApiClient.get(`/coupons/type/${couponType}`, {
        params: { order_amount: orderAmount },
        headers: getRequiredHeaders(),
      });

      return response.data as ICouponsResponse;
    } catch (error) {
      console.error('Error fetching coupons:', error);
      throw error;
    }
  },

  /**
   * Initiate payment via Stripe
   * @param subscriptionId - The subscription plan ID
   * @param payload - Payment request payload
   */
  payWithStripe: async (
    subscriptionId: string,
    payload: IPaymentRequest
  ): Promise<IStripePaymentResponse> => {
    try {
      const response = await ApiClient.post(
        `/subscriptions/${subscriptionId}/payments/stripe`,
        payload,
        {
          headers: getRequiredHeaders(),
        }
      );

      return response.data as IStripePaymentResponse;
    } catch (error) {
      console.error('Error initiating Stripe payment:', error);
      throw error;
    }
  },

  /**
   * Initiate payment via Paytrail
   * @param subscriptionId - The subscription plan ID
   * @param payload - Payment request payload (must include email)
   */
  payWithPaytrail: async (
    subscriptionId: string,
    payload: IPaymentRequest
  ): Promise<IPaytrailPaymentResponse> => {
    try {
      if (!payload.email) {
        throw new Error('Email is required for Paytrail payments');
      }

      const response = await ApiClient.post(
        `/subscriptions/${subscriptionId}/payments/paytrail`,
        payload,
        {
          headers: getRequiredHeaders(),
        }
      );

      return response.data as IPaytrailPaymentResponse;
    } catch (error) {
      console.error('Error initiating Paytrail payment:', error);
      throw error;
    }
  },

  /**
   * Activate freemium (free) subscription
   * @param subscriptionId - The subscription plan ID
   * @param payload - Payment request payload (for entity IDs)
   */
  activateFreemiumSubscription: async (
    subscriptionId: string,
    payload: IPaymentRequest
  ): Promise<IFreemiumResponse> => {
    try {
      const response = await ApiClient.post(
        `/subscriptions/${subscriptionId}/freemium`,
        payload,
        {
          headers: getRequiredHeaders(),
        }
      );

      return response.data as IFreemiumResponse;
    } catch (error) {
      console.error('Error activating freemium subscription:', error);
      throw error;
    }
  },

  getPricingSummary: async (
    payload: IPricingSummaryRequest
  ): Promise<IPricingSummaryResponse> => {
    try {
      const response = await ApiClient.post(
        '/subscriptions/pricing-summary',
        payload,
        {
          headers: getRequiredHeaders(),
        }
      );

      return response.data as IPricingSummaryResponse;
    } catch (error) {
      console.error('Error fetching pricing summary:', error);
      throw error;
    }
  },

  /**
   * Validate a coupon code
   * @param payload - Coupon validation request payload
   */
  validateCoupon: async (
    payload: ICouponValidationRequest
  ): Promise<ICouponValidationResponse> => {
    try {
      const response = await ApiClient.post(`/coupons/validate`, payload, {
        headers: getRequiredHeaders(),
      });

      return response.data as ICouponValidationResponse;
    } catch (error) {
      console.error('Error validating coupon:', error);
      throw error;
    }
  },
};
